import type {
  Position,
  PositionedItem,
} from '../../PageConfigProvider/types/item';
import { MIN_ITEM_HEIGHT_CELLS, MIN_ITEM_WIDTH_CELLS } from '../constants';

export type OccupancyMatrix = boolean[][];

export type GridDimensions = {
  rows: number;
  columns: number;
};

function isIdIgnored(
  id: string,
  ignoreItemId?: string,
  ignoreItemIds?: Set<string> | string[]
): boolean {
  if (ignoreItemId && id === ignoreItemId) return true;
  if (ignoreItemIds) {
    if (ignoreItemIds instanceof Set) return ignoreItemIds.has(id);
    return ignoreItemIds.includes(id);
  }
  return false;
}

/**
 * Creates a 2D matrix representing the grid, marking cells occupied by items as `true`.
 */
export function buildOccupancyMatrix(
  grid: GridDimensions,
  items: PositionedItem[],
  ignoreItemId?: string,
  ignoreItemIds?: Set<string> | string[]
): OccupancyMatrix {
  const matrix: OccupancyMatrix = Array.from({ length: grid.rows }, () =>
    Array(grid.columns).fill(false)
  );

  items.forEach((it) => {
    if (isIdIgnored(it.id, ignoreItemId, ignoreItemIds)) return;
    occupyArea(it.position, matrix);
  });

  return matrix;
}

/**
 * Marks the cells within a given rectangle as occupied (`true`) in the matrix.
 */
export function occupyArea(rect: Position, matrix: OccupancyMatrix): void {
  for (let y = rect.y; y < rect.y + rect.height; y++) {
    for (let x = rect.x; x < rect.x + rect.width; x++) {
      if (
        y >= 0 &&
        y < matrix.length &&
        x >= 0 &&
        x < (matrix[y]?.length ?? 0)
      ) {
        // @ts-expect-error noUncheckedIndexedAccess
        matrix[y][x] = true;
      }
    }
  }
}

/**
 * Checks if a given rectangular area is completely free in the occupancy matrix.
 */
export function isAreaFree(
  rect: Position,
  grid: GridDimensions,
  matrix: boolean[][]
): boolean {
  if (
    rect.x < 0 ||
    rect.y < 0 ||
    rect.x + rect.width > grid.columns ||
    rect.y + rect.height > grid.rows
  ) {
    return false;
  }

  for (let y = rect.y; y < rect.y + rect.height; y++) {
    for (let x = rect.x; x < rect.x + rect.width; x++) {
      if (matrix[y]?.[x]) return false;
    }
  }
  return true;
}

/**
 * Validates that a position is within the grid boundaries and meets minimum size requirements.
 */
export function validateBoundaries(
  grid: GridDimensions,
  position: Position
): boolean {
  return (
    position.x >= 0 &&
    position.y >= 0 &&
    position.width >= MIN_ITEM_WIDTH_CELLS &&
    position.height >= MIN_ITEM_HEIGHT_CELLS &&
    position.x + position.width <= grid.columns &&
    position.y + position.height <= grid.rows
  );
}

/**
 * Validates a position against grid boundaries and for collisions with other items.
 */
export function validatePosition(
  grid: GridDimensions,
  position: Position,
  items: PositionedItem[],
  ignoreItemId?: string,
  ignoreItemIds?: Set<string> | string[]
): boolean {
  if (!validateBoundaries(grid, position)) {
    return false;
  }

  const matrix = buildOccupancyMatrix(grid, items, ignoreItemId, ignoreItemIds);
  if (!isAreaFree(position, grid, matrix)) {
    return false;
  }

  return true;
}
